import { animate, state, style, transition, trigger } from '@angular/animations';
import { HttpParams } from '@angular/common/http';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    SimpleChange,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subscription, Subject, merge, timer, EMPTY } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { API_BASE_URL_SERVICE } from '../../../../kiene-core.config';
import { SoundType } from '../../../../classes/sound-type';
import { KieneBackendApiService } from '../../../../services/backend-api/kiene-backend-api.service';
import { AppService } from '../../../../services/app/app.service';
import { MessageService } from '../../../../services/message-service/message.service';
import { KieneUtils } from '../../../../utils/KieneUtils';
import { GruppeTier } from '../../../gruppe-tier/classes/gruppe-tier';
import { Tierliste } from '../../../gruppe-tier/classes/tierliste';
import { Gruppe } from '../../classes/gruppe';
import { TierFilterSettings } from '../../../gruppe-tier/filter/gruppe-tier-filter-dialog/classes/tier-filter-settings';
import { ConfirmDialogComponent } from '../../../dialogs/confirm-dialog/confirm-dialog.component';
import { ConfirmDialogInput, ConfirmDialogResult } from '../../../dialogs/confirm-dialog/confirm-dialog';
import { EnterValueDialogComponent } from '../../../dialogs/enter-value-dialog/enter-value-dialog.component';
import { EnterValueInput, EnterValueResponse } from '../../../dialogs/enter-value-dialog/enter-value-response';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { GruppenService } from '../../gruppen.service';
import {
    AbgangTierLookupOptions,
    appendAbgangTierLookupGruppeFilter,
    isAbgangTierlisteGlobal,
} from '../abgang-tier-lookup.util';
import { GruppeAbgangService } from '../gruppe-abgang.service';
import { normalizeOhrmarkePassScanInput, OhrmarkeScanTrailGuard } from '../../../../utils/ohrmarke-pass-scan.util';

@Component({
    selector: 'kiene-abgang-tier-eingabe',
    templateUrl: './abgang-tier-eingabe.component.html',
    styleUrls: ['./abgang-tier-eingabe.component.scss'],
    animations: [
        trigger('openClose', [
            state('open', style({ backgroundColor: 'rgba(55, 126, 34, 0.8)' })),
            state('closed', style({ backgroundColor: 'rgba(55, 126, 34, 0)' })),
            transition('open => closed', [animate('1s')]),
            transition('closed => open', [animate('0.5s')]),
            state('openWarning', style({ backgroundColor: 'rgba(255, 150, 45, 0.8)' })),
            state('closedWarning', style({ backgroundColor: 'rgba(255, 150, 45, 0)' })),
            transition('openWarning => closedWarning', [animate('1s')]),
            transition('closedWarning => openWarning', [animate('0.5s')]),
            state('openError', style({ backgroundColor: 'rgba(155, 0, 36, 0.8)' })),
            state('closedError', style({ backgroundColor: 'rgba(155, 0, 36, 0)' })),
            transition('openError => closedError', [animate('1s')]),
            transition('closedError => openError', [animate('0.5s')]),
        ]),
    ],
})
export class AbgangTierEingabeComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('tierEingabeInput', { read: ElementRef })
    tierEingabeInput?: ElementRef<HTMLInputElement>;

    @Input('abgangsliste') abgangsliste: Tierliste;
    @Input('isVisible') isVisible = true;
    /** Tighter layout for dashboard stepper (less padding, no empty scan panel). */
    @Input() compact = false;
    /** Dashboard stepper: no inner list scroll — parent dialog scrolls the whole step. */
    @Input() stepperLayout = false;
    /** Groups from the Gruppe step (one id or all groups when „Global“ was chosen). */
    @Input() tierBrowseGruppeIds: number[] | null = null;
    /** Optional group context for filter option loading. */
    @Input() filterGruppeId: number | null = null;
    /** Animals currently in the source group (for bulk add to Ausstallliste). */
    @Input() anzahlTiereInGruppe = 0;

    eingabeCtrl = new UntypedFormControl('');
    mitGewichtToggleCtrl = new UntypedFormControl(false);

    numbers = '';
    fontSize = '2em';
    checkBackgroundColor = 'lightgray';
    checkColor = 'gray';
    checkBorderColor = 'black';
    checkIcon = '';
    ohrmarke = '';
    ohrmarkeGefunden = false;
    keinErgebnis = false;
    /** Matches last N digits of ear tag / pass (Enter adds only from here). */
    ohrmarken: GruppeTier[] = [];
    /** Broader substring matches, excluding animals already in `ohrmarken`. */
    ohrmarkenAlle: GruppeTier[] = [];
    gruppenTiere: GruppeTier[] = [];
    anzeigeTiere: GruppeTier[] = [];
    gruppenTiereLaden = false;
    gruppenTiereSucheLaeuft = false;
    alleTiereZurListeLaeuft = false;
    tierLoadMoreRunning = false;
    tierHasMore = false;
    tierTotalCount = 0;
    /** Loaded rows for current „alle“ or „teil“ query (for pagination). */
    tierOffset = 0;
    readonly tierPageSize = 100;
    selectedTier?: GruppeTier;
    /** Blocks debounced input while a scan/add request is in flight (prevents digits reappearing). */
    private scanBusy = false;
    /** Digits from the scan that was submitted (input cleared immediately like Einstallen). */
    private pendingScanDigits = '';
    /** Cancels pending debounced input when a scan clears the field (prevents digits reappearing). */
    private readonly eingabeCancel$ = new Subject<void>();
    private readonly trailGuard = new OhrmarkeScanTrailGuard();

    tierlisteTiere: GruppeTier[] = [];
    tierlisteTiereLaden = false;
    openCloseState = 'closed';
    filterSettings: TierFilterSettings = this.createInitialFilterSettings();
    filterDialogGruppe: Gruppe | null = null;

    private readonly ohrmarkeLength = 10;
    private readonly passLength = 15;
    private ladeOhrmarkeSub?: Subscription;
    private ladeGruppenTiereSub?: Subscription;
    private ladeWeitereTrefferSub?: Subscription;
    private eingabeSub?: Subscription;
    private abgangslisteSub?: Subscription;
    private ladeTierlisteTiereSub?: Subscription;
    private inflightTierlisteKey = '';
    private inflightGruppenKey = '';
    private inflightCountKey = '';
    private ladeAlleTiereZurListeCountSub?: Subscription;
    /** Tiere der Bulk-Gruppe, die noch auf keine Ausstallliste gesetzt sind. */
    alleTiereZurListeVerfuegbarCount: number | null = null;
    alleTiereZurListeCountLaden = false;
    /** `idle` = no list yet; `alle` = browse all animals; `teil` = partial ear-tag search. */
    tierListeModus: 'idle' | 'alle' | 'teil' = 'idle';
    private alleTierPaginierung = { total: 0, offset: 0, hasMore: false };

    constructor(
        private readonly api: KieneBackendApiService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly abgangService: GruppeAbgangService,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
        private readonly appService: AppService,
        private readonly gruppenService: GruppenService,
        private readonly localStorageService: LocalStorageService,
        private readonly cdr: ChangeDetectorRef,
        private readonly host: ElementRef<HTMLElement>,
    ) {}

    get maxLengthEingabe(): number {
        return this.passLength;
    }

    get eingabeLabel(): string {
        return 'Ohrmarke oder Pass scannen, eingeben oder suchen';
    }

    get restrictToGruppeHint(): string {
        if (this.isGlobalAlleGruppenBrowse()) {
            const n = this.getEffectiveBrowseGruppeIds().length;
            return `Tiere aus ${n} Gruppen – Suche durchsucht alle gewählten Gruppen`;
        }
        if (this.isGlobalTierliste) {
            return 'Tiere aus allen Gruppen des Betriebs';
        }
        if (this.getEffectiveBrowseGruppeIds().length === 1) {
            return 'Ohrmarke, Pass und Suche nur in der gewählten Gruppe';
        }
        return 'Ohrmarke, Pass und Suche nur in der gewählten Gruppe';
    }

    get isTeilOhrmarkeSuche(): boolean {
        const len = this.numbers.length;
        return len > 0 && len < this.ohrmarkeLength;
    }

    get showGruppenTiereListe(): boolean {
        return !!this.abgangsliste?.tierliste_id;
    }

    get canConfirmEingabe(): boolean {
        const len = this.readDigitsFromInput().length;
        return (
            len === this.ohrmarkeLength ||
            len === this.passLength ||
            (this.ohrmarkeGefunden && this.selectedTier != null && this.isLetzte5SucheAktiv)
        );
    }

    /** Partial search with at least one digit (last-5 suffix logic). */
    get isLetzte5SucheAktiv(): boolean {
        const len = this.numbers.length;
        return len > 0 && len < this.ohrmarkeLength;
    }

    get showAlleTiereButton(): boolean {
        const kannLaden =
            this.tierListeModus === 'idle' ||
            (this.tierListeModus === 'alle' && this.gruppenTiere.length === 0 && !this.gruppenTiereLaden);
        return (
            this.showGruppenTiereListe &&
            this.numbers.length === 0 &&
            kannLaden &&
            !this.gruppenTiereLaden &&
            this.canBrowseTiereForListe()
        );
    }

    get isAbgangslisteOpen(): boolean {
        if (!this.abgangsliste?.tierliste_id) {
            return false;
        }
        const statusId = Number(this.abgangsliste?.status_id);
        if (Number.isNaN(statusId) || statusId <= 0) {
            return true;
        }
        return statusId < 162;
    }

    get showAlleTiereZurListeButton(): boolean {
        return this.showGruppenTiereListe && this.isAbgangslisteOpen && !this.isGlobalTierliste;
    }

    get alleTiereZurListeCount(): number {
        return this.alleTiereZurListeVerfuegbarCount ?? 0;
    }

    get canAlleTiereZurListeHinzufuegen(): boolean {
        return (
            !this.alleTiereZurListeCountLaden &&
            this.alleTiereZurListeCount > 0 &&
            this.getGruppeIdForBulkAdd() > 0
        );
    }

    private getGruppeIdForBulkAdd(): number {
        const fromListe = Number(this.abgangsliste?.gruppe_id_abgang || 0);
        if (fromListe > 0) {
            return fromListe;
        }
        const fromFilter = Number(this.filterGruppeId || 0);
        if (fromFilter > 0) {
            return fromFilter;
        }
        const browseIds = this.getEffectiveBrowseGruppeIds();
        return browseIds.length > 0 ? Number(browseIds[0]) : 0;
    }

    get showAbgangslisteLeerenButton(): boolean {
        if (!this.isAbgangslisteOpen) {
            return false;
        }
        if (Number(this.abgangsliste?.mehrere_gruppen || 0) > 0) {
            const userId = this.localStorageService.getCurrentUser()?.benutzer_id;
            return KieneUtils.sameId(this.abgangsliste?.erstellt_von, userId);
        }
        return true;
    }

    get canAbgangslisteLeeren(): boolean {
        return Number(this.abgangsliste?.anzahl_tiere || 0) > 0 || this.tierlisteTiere.length > 0;
    }

    get hideKundeOnScanTierCard(): boolean {
        return KieneUtils.isApiZero(this.abgangsliste?.mehrere_gruppen);
    }

    get canRemoveTierFromListe(): boolean {
        return this.isAbgangslisteOpen;
    }

    get tierlisteTiereAnzeigeCount(): number {
        return Math.max(this.tierlisteTiere.length, Number(this.abgangsliste?.anzahl_tiere || 0));
    }

    get tierListenInfo(): string {
        if (this.tierTotalCount <= 0) {
            return '';
        }
        const geladen =
            this.tierListeModus === 'teil'
                ? this.ohrmarken.length + this.ohrmarkenAlle.length
                : this.gruppenTiere.length;
        const scope = this.isGlobalTierliste || this.isGlobalAlleGruppenBrowse() ? 'alle Gruppen' : 'der Gruppe';
        if (this.tierTotalCount > geladen) {
            return `${geladen} von ${this.tierTotalCount} Tieren (${scope}) geladen`;
        }
        return `${this.tierTotalCount} Tiere (${scope})`;
    }

    get tierListenColTitle(): string {
        return this.tierListenInfo || 'Tiere';
    }

    /** Initial state: nothing loaded, nothing scanned, no active search. */
    get showUnifiedListsEmpty(): boolean {
        if (this.gruppenTiereLaden || this.gruppenTiereSucheLaeuft || this.tierlisteTiereLaden || this.numbers.length > 0) {
            return false;
        }
        if (this.tierlisteTiere.length > 0 || Number(this.abgangsliste?.anzahl_tiere || 0) > 0) {
            return false;
        }
        if (this.anzeigeTiere.length > 0 || this.ohrmarken.length > 0 || this.ohrmarkenAlle.length > 0) {
            return false;
        }
        if (this.tierListeModus === 'alle' || this.tierListeModus === 'teil') {
            return false;
        }
        return true;
    }

    get isGlobalTierliste(): boolean {
        return isAbgangTierlisteGlobal(this.abgangsliste);
    }

    get mitGewichtBeimHinzufuegen(): boolean {
        return !!this.mitGewichtToggleCtrl.value;
    }

    toggleMitGewicht(): void {
        this.mitGewichtToggleCtrl.setValue(!this.mitGewichtToggleCtrl.value);
    }

    getTierZusatzInfo(tier: GruppeTier): string {
        const gruppe = String(tier?.gruppe || '').trim();
        return gruppe.length > 0 ? `Gruppe: ${gruppe}` : '';
    }

    ngOnInit(): void {
        this.updateFilterDialogGruppe();
        this.ladeTierlisteTiere();
        this.ladeInitialeGruppenTiere();
        this.ladeAlleTiereZurListeVerfuegbarCount();
        this.eingabeSub = merge(
            this.eingabeCtrl.valueChanges.pipe(map(value => ({ kind: 'value' as const, value }))),
            this.eingabeCancel$.pipe(map(() => ({ kind: 'cancel' as const }))),
        )
            .pipe(
                switchMap(event => {
                    if (event.kind === 'cancel') {
                        return EMPTY;
                    }
                    return timer(300).pipe(map(() => event.value));
                }),
            )
            .subscribe(value => {
                if (this.scanBusy || this.trailGuard.shouldIgnoreIncoming()) {
                    if (this.trailGuard.shouldIgnoreIncoming() && value) {
                        this.eingabeCtrl.setValue('', { emitEvent: false });
                        this.numbers = '';
                    }
                    return;
                }
                this.onEingabeChanged(this.readDigitsFromInput(value));
            });

        this.abgangslisteSub = this.abgangService.watchAbgangsliste().subscribe(reload => {
            if (reload?.reloadAbgangsliste) {
                this.ladeTierlisteTiere(reload.tierliste);
            }
            if (reload?.reloadGruppenTiere) {
                this.ladeAlleTiereZurListeVerfuegbarCount();
                if (this.tierListeModus === 'alle' || this.tierListeModus === 'teil') {
                    this.reloadAktuelleTierListe();
                } else {
                    this.ladeInitialeGruppenTiere();
                }
            } else if (reload?.reloadAbgangsliste) {
                this.ladeAlleTiereZurListeVerfuegbarCount();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['abgangsliste'] && this.hatAbgangslisteGewechselt(changes['abgangsliste'])) {
            this.resetTierListeState();
            this.ladeTierlisteTiere();
            this.ladeInitialeGruppenTiere();
            this.ladeAlleTiereZurListeVerfuegbarCount();
        }
        if (changes['tierBrowseGruppeIds'] && this.hatBrowseGruppeIdsGewechselt(changes['tierBrowseGruppeIds'])) {
            this.resetTierListeState();
            this.updateFilterDialogGruppe();
            this.ladeInitialeGruppenTiere();
            this.ladeAlleTiereZurListeVerfuegbarCount();
        }
        if (changes['filterGruppeId']) {
            this.updateFilterDialogGruppe();
            this.ladeAlleTiereZurListeVerfuegbarCount();
        }
        if (changes['anzahlTiereInGruppe']) {
            this.ladeAlleTiereZurListeVerfuegbarCount();
        }
    }

    private hatAbgangslisteGewechselt(change: SimpleChange | undefined): boolean {
        if (!change) {
            return false;
        }
        const prevId = Number(change.previousValue?.tierliste_id || 0);
        const currId = Number(change.currentValue?.tierliste_id || 0);
        return prevId !== currId;
    }

    private hatBrowseGruppeIdsGewechselt(change: SimpleChange | undefined): boolean {
        if (!change) {
            return false;
        }
        return this.serializeGruppeIds(change.previousValue) !== this.serializeGruppeIds(change.currentValue);
    }

    private serializeGruppeIds(ids: number[] | null | undefined): string {
        return (ids || [])
            .map(id => Number(id))
            .filter(id => id > 0)
            .sort((a, b) => a - b)
            .join(',');
    }

    ngAfterViewInit(): void {
        this.focusInput();
    }

    ngOnDestroy(): void {
        this.trailGuard.destroy();
        this.ladeOhrmarkeSub?.unsubscribe();
        this.ladeGruppenTiereSub?.unsubscribe();
        this.ladeWeitereTrefferSub?.unsubscribe();
        this.ladeTierlisteTiereSub?.unsubscribe();
        this.ladeAlleTiereZurListeCountSub?.unsubscribe();
        this.eingabeSub?.unsubscribe();
        this.abgangslisteSub?.unsubscribe();
        this.eingabeCancel$.complete();
    }

    /** Count of animals in the bulk-add group that are not yet on any Ausstallliste. */
    private ladeAlleTiereZurListeVerfuegbarCount(): void {
        const gruppeId = this.getGruppeIdForBulkAdd();
        if (gruppeId <= 0 || !this.abgangsliste?.tierliste_id || this.isGlobalTierliste) {
            this.alleTiereZurListeVerfuegbarCount = null;
            this.alleTiereZurListeCountLaden = false;
            return;
        }

        const countKey = `${gruppeId}|${this.abgangsliste.tierliste_id}`;
        if (this.ladeAlleTiereZurListeCountSub && !this.ladeAlleTiereZurListeCountSub.closed && this.inflightCountKey === countKey) {
            return;
        }
        this.inflightCountKey = countKey;
        this.alleTiereZurListeCountLaden = true;
        this.ladeAlleTiereZurListeCountSub?.unsubscribe();
        const params = new HttpParams()
            .set('gruppe_id', String(gruppeId))
            .set('lebendig_nicht_abgegangen', '1')
            .set('limit', '1')
            .set('offset', '0');
        this.ladeAlleTiereZurListeCountSub = this.api
            .getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params)
            .subscribe({
                next: response => {
                    this.alleTiereZurListeVerfuegbarCount = Number(response?.count ?? 0);
                    this.alleTiereZurListeCountLaden = false;
                    this.cdr.markForCheck();
                },
                error: () => {
                    this.alleTiereZurListeVerfuegbarCount = null;
                    this.alleTiereZurListeCountLaden = false;
                    this.cdr.markForCheck();
                },
            });
    }

    private adjustAlleTiereZurListeVerfuegbarCount(delta: number): void {
        if (this.alleTiereZurListeVerfuegbarCount == null) {
            return;
        }
        this.alleTiereZurListeVerfuegbarCount = Math.max(0, this.alleTiereZurListeVerfuegbarCount + delta);
    }

    trackTierByGtId(_index: number, tier: GruppeTier): number {
        return Number(tier?.gt_id) || _index;
    }

    ladeTierlisteTiere(tierlisteOverride?: Tierliste): void {
        const liste = tierlisteOverride ?? this.abgangsliste;
        const tierlisteId = Number(liste?.tierliste_id || 0);
        if (tierlisteId <= 0) {
            this.tierlisteTiere = [];
            this.tierlisteTiereLaden = false;
            return;
        }

        const listKey = `${tierlisteId}|${this.tierPageSize}`;
        if (this.ladeTierlisteTiereSub && !this.ladeTierlisteTiereSub.closed && this.inflightTierlisteKey === listKey) {
            return;
        }
        this.inflightTierlisteKey = listKey;
        this.tierlisteTiereLaden = true;
        this.ladeTierlisteTiereSub?.unsubscribe();
        const scrollTop = this.captureDialogScrollTop();
        let params = new HttpParams()
            .set('tierliste_id', String(tierlisteId))
            .set('orderby', 'editiert desc')
            .set('limit', String(this.tierPageSize))
            .set('offset', '0');
        this.ladeTierlisteTiereSub = this.api
            .getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params)
            .subscribe({
                next: response => {
                    this.tierlisteTiere = response.records || [];
                    this.tierlisteTiereLaden = false;
                    this.syncBrowseListTierlisteIdsFromTierlisteTiere();
                    this.restoreDialogScrollTop(scrollTop);
                    this.cdr.markForCheck();
                },
                error: err => {
                    this.tierlisteTiereLaden = false;
                    this.restoreDialogScrollTop(scrollTop);
                    this.messageService.errorMessage('Fehler beim Laden der Ausstallliste!', err);
                    this.cdr.markForCheck();
                },
            });
    }

    /** Loads the first page of group animals for the left column (excludes animals already on the list). */
    private ladeInitialeGruppenTiere(): void {
        if (!this.canBrowseTiereForListe()) {
            return;
        }
        this.fetchTierAlle(false);
    }

    ladeAlleTiere(): void {
        this.fetchTierAlle(false);
    }

    ladeWeitereTiere(): void {
        if (this.tierListeModus === 'teil') {
            this.sucheTeilOhrmarkeViaApi(true);
        } else if (this.tierListeModus === 'alle') {
            this.fetchTierAlle(true);
        }
    }

    isTierAufAktuellerListe(tier: GruppeTier): boolean {
        const gtId = Number(tier?.gt_id || 0);
        if (gtId <= 0) {
            return false;
        }
        const listeId = Number(this.abgangsliste?.tierliste_id || 0);
        if (listeId > 0 && Number(tier?.tierliste_id) === listeId) {
            return true;
        }
        return this.tierlisteTiere.some(t => Number(t?.gt_id) === gtId);
    }

    tierListeAktion(tier: GruppeTier, event: Event): void {
        event.stopPropagation();
        this.selectedTier = tier;
        if (this.isTierAufAktuellerListe(tier)) {
            this.ausListeEntfernen();
            return;
        }
        if (this.mitGewichtBeimHinzufuegen) {
            this.openWeightDialog();
            return;
        }
        this.tierZurListeHinzufuegen();
    }

    @HostListener('window:keyup', ['$event'])
    catchKeyboardEvent(event: KeyboardEvent): void {
        if (!this.isVisible || this.isKeyEventFromInput(event) || this.scanBusy || this.trailGuard.shouldIgnoreIncoming()) {
            return;
        }
        if (event.code === 'Backspace') {
            this.backspace();
            return;
        }
        if (event.code.startsWith('Numpad') || event.code.startsWith('Digit')) {
            const splitted = event.code.split(/Numpad|Digit/);
            if (splitted?.[1] && this.numbers.length < this.maxLengthEingabe) {
                this.onEingabeChanged(this.numbers + splitted[1]);
            }
        }
    }

    backspace(): void {
        if (this.numbers.length > 0) {
            this.onEingabeChanged(this.numbers.substring(0, this.numbers.length - 1));
        }
    }

    onEingabeEnter(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.trailGuard.settleThenRun(
            () => String(this.eingabeCtrl.value ?? this.numbers),
            () => {
                this.syncEingabeFromControl();
                this.submitEingabe();
            },
        );
    }

    submitEingabe(): void {
        if (!this.abgangsliste?.tierliste_id || this.scanBusy) {
            return;
        }
        this.syncEingabeFromControl();
        const len = this.numbers.length;
        if (len === this.passLength || len === this.ohrmarkeLength) {
            const digits = this.beginScanSession(this.numbers);
            this.resolveExactAndAdd(digits);
            return;
        }
        if (this.ohrmarkeGefunden && this.selectedTier && this.isLetzte5SucheAktiv) {
            const tier = this.selectedTier;
            this.beginScanSession(this.numbers);
            this.selectedTier = tier;
            if (this.isTierAufAktuellerListe(tier)) {
                this.ausListeEntfernen();
                return;
            }
            this.tierZurListeHinzufuegen();
        }
    }

    addOhrmarkePass(event: Event): void {
        event.stopPropagation();
        this.trailGuard.settleThenRun(
            () => String(this.eingabeCtrl.value ?? this.numbers),
            () => {
                this.syncEingabeFromControl();
                this.submitEingabe();
            },
        );
    }

    private readDigitsFromInput(raw?: unknown): string {
        const source = raw !== undefined ? raw : this.eingabeCtrl.value;
        const digits = String(source ?? '').replace(/\D/g, '').slice(0, this.maxLengthEingabe);
        return normalizeOhrmarkePassScanInput(digits);
    }

    /** Like einstallen: read control value on Enter before submit (scanner sends digits + Enter quickly). */
    private syncEingabeFromControl(): void {
        const digits = this.readDigitsFromInput();
        if (digits !== this.numbers) {
            this.onEingabeChanged(digits);
        }
    }

    private onEingabeChanged(digits: string): void {
        if (this.scanBusy || this.trailGuard.shouldIgnoreIncoming()) {
            return;
        }
        const maxLen = this.maxLengthEingabe;
        this.numbers = digits.slice(0, maxLen);
        this.eingabeCtrl.setValue(this.numbers, { emitEvent: false });
        this.calcFontSize();

        if (!this.abgangsliste?.tierliste_id) {
            return;
        }

        const len = this.numbers.length;

        if (len > 0 && len < this.ohrmarkeLength) {
            this.sucheTeilOhrmarkeInGruppenTieren();
            return;
        }

        if (len >= this.ohrmarkeLength) {
            this.aktualisiereAnzeigeTiere();
            return;
        }

        if (len === 0) {
            this.resetTrefferAnzeige();
            if (this.tierListeModus === 'teil') {
                if (this.gruppenTiere.length > 0) {
                    this.tierListeModus = 'alle';
                    this.tierTotalCount = this.alleTierPaginierung.total;
                    this.tierOffset = this.alleTierPaginierung.offset;
                    this.tierHasMore = this.alleTierPaginierung.hasMore;
                } else {
                    this.tierListeModus = 'idle';
                    this.tierOffset = 0;
                    this.tierHasMore = false;
                    this.tierTotalCount = 0;
                }
            }
            this.aktualisiereAnzeigeTiere();
        }
    }

    private getEffectiveBrowseGruppeIds(): number[] {
        const fromInput = (this.tierBrowseGruppeIds || []).map(id => Number(id)).filter(id => id > 0);
        if (fromInput.length > 0) {
            return [...new Set(fromInput)];
        }
        if (!this.isGlobalTierliste) {
            const gid = Number(this.abgangsliste?.gruppe_id_abgang || 0);
            return gid > 0 ? [gid] : [];
        }
        return [];
    }

    private get tierLookupOptions(): AbgangTierLookupOptions {
        return {
            browseGruppeIds: this.getEffectiveBrowseGruppeIds(),
            kundeIdFallback: Number(this.localStorageService.getCurrentClient()?.kunde_id || 0) || undefined,
        };
    }

    private canBrowseTiereForListe(): boolean {
        if (!this.abgangsliste?.tierliste_id) {
            return false;
        }
        return this.isGlobalTierliste || this.getEffectiveBrowseGruppeIds().length > 0;
    }

    /** „Global“ on Gruppe step or global tierliste: multiple groups from browse scope. */
    isGlobalAlleGruppenBrowse(): boolean {
        return this.getEffectiveBrowseGruppeIds().length > 1;
    }

    private resetTierListeState(): void {
        this.ladeGruppenTiereSub?.unsubscribe();
        this.inflightGruppenKey = '';
        this.ladeWeitereTrefferSub?.unsubscribe();
        this.gruppenTiere = [];
        this.tierOffset = 0;
        this.tierHasMore = false;
        this.tierTotalCount = 0;
        this.tierListeModus = 'idle';
        this.alleTierPaginierung = { total: 0, offset: 0, hasMore: false };
        this.gruppenTiereLaden = false;
        this.tierLoadMoreRunning = false;
        this.aktualisiereAnzeigeTiere();
        this.cdr.markForCheck();
    }

    onTierFilterSelected(filterSettings: TierFilterSettings): void {
        this.filterSettings = filterSettings?.clone?.() ?? this.createInitialFilterSettings();
        // Keep "Abgangsliste" hidden/ignored in this flow.
        this.filterSettings.showAbgangslisten = false;
        this.reloadAktuelleTierListe();
    }

    private reloadAktuelleTierListe(): void {
        if (this.tierListeModus === 'alle') {
            this.fetchTierAlle(false);
        } else if (this.tierListeModus === 'teil' && this.numbers.length > 0) {
            this.sucheTeilOhrmarkeViaApi(false);
        }
    }

    private buildBaseTierListeParams(): HttpParams {
        let params = appendAbgangTierLookupGruppeFilter(new HttpParams(), this.abgangsliste, this.tierLookupOptions);
        params = this.appendTierFilterParams(params);
        return params
            .set('tot', '0')
            .set('tierliste_id_or_null', String(this.abgangsliste!.tierliste_id))
            .set('limit', String(this.tierPageSize));
    }

    private appendTierFilterParams(params: HttpParams): HttpParams {
        if (!this.filterSettings) {
            return params;
        }
        const filterParams = this.filterSettings.getAsParams();
        for (const key of filterParams.keys()) {
            if (key === 'tierliste_id[]') {
                continue;
            }
            const values = filterParams.getAll(key) || [];
            if (values.length === 0) {
                continue;
            }
            for (const value of values) {
                params = params.append(key, value);
            }
        }
        return params;
    }

    private applyTierSeiteResponse(
        response: { records?: GruppeTier[]; count?: number },
        append: boolean,
        recordsTarget: 'gruppen' | 'teil',
    ): void {
        const records = response.records || [];
        const total = response.count ?? records.length;
        if (recordsTarget === 'gruppen') {
            this.gruppenTiere = append ? [...this.gruppenTiere, ...records] : records;
            this.tierOffset = this.gruppenTiere.length;
        } else {
            this.ohrmarken = append ? [...this.ohrmarken, ...records] : records;
            this.tierOffset = this.ohrmarken.length;
        }
        this.tierTotalCount = total;
        this.tierHasMore = this.tierOffset < total;
    }

    private fetchTierAlle(append: boolean): void {
        if (!this.canBrowseTiereForListe()) {
            return;
        }

        const offset = append ? this.tierOffset : 0;
        const params = this.buildBaseTierListeParams().set('offset', String(offset));
        const gruppenKey = params.toString();
        if (
            !append &&
            this.ladeGruppenTiereSub &&
            !this.ladeGruppenTiereSub.closed &&
            this.inflightGruppenKey === gruppenKey
        ) {
            return;
        }
        this.inflightGruppenKey = gruppenKey;

        if (append) {
            this.tierLoadMoreRunning = true;
        } else {
            this.gruppenTiereLaden = true;
            this.tierListeModus = 'alle';
            this.tierOffset = 0;
            this.tierHasMore = false;
            this.tierTotalCount = 0;
        }

        this.ladeGruppenTiereSub?.unsubscribe();
        this.ladeGruppenTiereSub = this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params).subscribe({
            next: response => {
                this.gruppenTiereLaden = false;
                this.tierLoadMoreRunning = false;
                this.applyTierSeiteResponse(response, append, 'gruppen');
                this.alleTierPaginierung = {
                    total: this.tierTotalCount,
                    offset: this.tierOffset,
                    hasMore: this.tierHasMore,
                };
                this.aktualisiereAnzeigeTiere();
                this.cdr.markForCheck();
            },
            error: err => {
                this.gruppenTiereLaden = false;
                this.tierLoadMoreRunning = false;
                this.messageService.errorMessage('Fehler!', err);
                this.cdr.markForCheck();
            },
        });
    }

    private aktualisiereAnzeigeTiere(): void {
        if (this.isTeilOhrmarkeSuche) {
            this.anzeigeTiere = [];
            return;
        }
        if (this.selectedTier && this.numbers.length >= this.ohrmarkeLength) {
            this.anzeigeTiere = [this.selectedTier];
            return;
        }
        if (this.showGruppenTiereListe) {
            this.anzeigeTiere = this.gruppenTiere;
            return;
        }
        this.anzeigeTiere = [];
    }

    private restoreListAfterFailedAdd(): void {
        this.aktualisiereAnzeigeTiere();
        this.cdr.markForCheck();
    }

    private isPassEingabe(digits?: string): boolean {
        return (digits ?? this.pendingScanDigits ?? this.numbers).length === this.passLength;
    }

    /** Full ear-tag or passport scan (not 5-digit partial search). */
    private isVolleingabeScan(digits?: string): boolean {
        const len = (digits ?? this.pendingScanDigits ?? this.numbers).length;
        return len === this.ohrmarkeLength || len === this.passLength;
    }

    private isTierBereitsAufAktuellerListe(): boolean {
        return !!this.selectedTier && this.isTierAufAktuellerListe(this.selectedTier);
    }

    private sucheTeilOhrmarkeInGruppenTieren(): void {
        this.sucheTeilOhrmarkeViaApi(false);
    }

    /** Server search across all animals in selected group(s), not only the loaded page. */
    private sucheTeilOhrmarkeViaApi(append: boolean): void {
        const sucheNach = this.numbers;
        if (!this.abgangsliste?.tierliste_id || !sucheNach) {
            return;
        }

        const offset = append ? this.tierOffset : 0;
        const params = this.buildBaseTierListeParams()
            .set('ohrmarke5', sucheNach)
            .set('offset', String(offset));

        this.ladeOhrmarkeSub?.unsubscribe();
        if (append) {
            this.tierLoadMoreRunning = true;
        } else {
            this.gruppenTiereSucheLaeuft = true;
            this.tierListeModus = 'teil';
            this.tierOffset = 0;
            this.tierHasMore = false;
            this.tierTotalCount = 0;
            this.ohrmarken = [];
            this.ohrmarkenAlle = [];
        }

        this.ladeOhrmarkeSub = this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params).subscribe({
            next: response => {
                this.gruppenTiereSucheLaeuft = false;
                this.tierLoadMoreRunning = false;
                if (this.numbers !== sucheNach || !this.isTeilOhrmarkeSuche) {
                    return;
                }
                this.applyTierSeiteResponse(response, append, 'teil');
                this.checkOhrmarkenVorschau();
                this.fetchWeitereTeilTreffer(sucheNach);
                this.aktualisiereAnzeigeTiere();
                this.cdr.markForCheck();
            },
            error: err => {
                this.gruppenTiereSucheLaeuft = false;
                this.tierLoadMoreRunning = false;
                this.messageService.errorMessage('Fehler!', err);
                this.cdr.markForCheck();
            },
        });
    }

    /** Broader substring matches (ear tag / pass), separate from last-5 API filter. */
    private fetchWeitereTeilTreffer(sucheNach: string): void {
        if (!this.abgangsliste?.tierliste_id) {
            return;
        }
        const params = this.buildBaseTierListeParams().set('search', sucheNach).set('offset', '0');
        const letzte5Ids = new Set(this.ohrmarken.map(t => t.gt_id));

        this.ladeWeitereTrefferSub?.unsubscribe();
        this.ladeWeitereTrefferSub = this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params).subscribe({
            next: response => {
                if (this.numbers !== sucheNach || !this.isTeilOhrmarkeSuche) {
                    return;
                }
                const records = (response.records || []).filter(
                    tier => this.tierMatchesBeliebigeStelle(tier, sucheNach) && !letzte5Ids.has(tier.gt_id),
                );
                this.ohrmarkenAlle = records;
                this.cdr.markForCheck();
            },
            error: () => {
                this.ohrmarkenAlle = [];
            },
        });
    }

    private tierMatchesBeliebigeStelle(tier: GruppeTier, suche: string): boolean {
        const ids = this.tierOhrmarkenIds(tier);
        return ids.some(id => id.includes(suche));
    }

    private tierOhrmarkenIds(tier: GruppeTier): string[] {
        const kurz = String(tier?.ohrmarke_kurz || '').replace(/\D/g, '');
        const voll = String(tier?.ohrmarke_notnull || '').replace(/\D/g, '');
        const pass = String(tier?.ohrmarke15 || '').replace(/\D/g, '');
        return [kurz, voll, pass].filter(id => id.length > 0);
    }

    private resolveExactAndAdd(scanDigits: string): void {
        let params = appendAbgangTierLookupGruppeFilter(new HttpParams(), this.abgangsliste, this.tierLookupOptions)
            .set('tot', '0')
            .set('tierliste_id_or_null', String(this.abgangsliste!.tierliste_id));
        if (this.isPassEingabe(scanDigits)) {
            params = params.set('ohrmarke15', scanDigits);
        } else if (scanDigits.length === this.ohrmarkeLength) {
            params = params.set('ohrmarke_kurz', scanDigits);
        } else {
            this.endScanSession();
            return;
        }

        this.ladeOhrmarkeSub?.unsubscribe();
        this.ladeOhrmarkeSub = this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params).subscribe({
            next: response => {
                if (response.count === 1) {
                    const tier = response.records[0];
                    if (this.isTierAufAktuellerListe(tier)) {
                        this.handleTierBereitsAufListe(scanDigits, tier);
                        return;
                    }
                    this.selectedTier = tier;
                    if (this.mitGewichtBeimHinzufuegen) {
                        this.openWeightDialog();
                        return;
                    }
                    this.tierZurListeHinzufuegen();
                    return;
                }
                if (response.count > 1) {
                    this.messageService.alertMessage('Mehr als ein Tier gefunden!');
                    this.pushScanErgebnis('error', scanDigits);
                    this.endScanSession();
                    return;
                }
                this.messageService.alertMessage('Kein Tier mit diesen Daten gefunden!');
                this.pushScanErgebnis('error', scanDigits);
                this.endScanSession();
            },
            error: err => {
                this.messageService.errorMessage('Fehler!', err);
                this.pushScanErgebnis('error', scanDigits);
                this.restoreListAfterFailedAdd();
                this.endScanSession();
            },
        });
    }

    private tierZurListeHinzufuegen(weight?: number, abteil?: string): void {
        if (!this.selectedTier?.gt_id || !this.abgangsliste?.tierliste_id) {
            this.endScanSession();
            return;
        }
        // Partial search only: already on this list → offer remove (like number pad).
        if (this.isTierBereitsAufAktuellerListe() && !this.isVolleingabeScan()) {
            this.ausListeEntfernen();
            return;
        }

        const scanOhrmarke = this.buildScanOhrmarkeAnzeige();
        const tierToAdd = this.selectedTier;

        this.appService.setBlockOnLoading(true);
        const obj = {
            tierliste_id: this.abgangsliste.tierliste_id,
            gruppen_tiere: [{ gt_id: tierToAdd.gt_id, abgangsgewicht: weight, abteil_abgang: abteil }],
            gruppe_id: this.abgangsliste?.gruppe_id_abgang ?? undefined,
        };
        this.api.post(this.apiBaseUrl + 'tierliste/abgang_kundenportal.php', obj, undefined).subscribe({
            next: tierlisteAntwort => {
                this.appService.setBlockOnLoading(false);
                this.pushScanErgebnis('ok', scanOhrmarke);
                const preserveBrowseList = this.shouldPreserveBrowseListOnAdd();
                this.adjustAlleTiereZurListeVerfuegbarCount(-1);
                this.prependTierZurAusstalllisteAnsicht(tierToAdd, tierlisteAntwort);
                this.abgangService.setAbgangsliste(tierlisteAntwort, false, false, preserveBrowseList);
                this.endScanSession();
                this.cdr.markForCheck();
            },
            error: err => {
                this.appService.setBlockOnLoading(false);
                this.messageService.errorMessage('Fehler!', err);
                this.pushScanErgebnis('error', scanOhrmarke);
                this.restoreListAfterFailedAdd();
                this.endScanSession();
            },
        });
    }

    private buildScanOhrmarkeAnzeige(scanDigits?: string): string {
        const digits = scanDigits ?? this.pendingScanDigits ?? this.numbers;
        if (digits.length === this.passLength || this.isPassEingabe(digits)) {
            return this.selectedTier?.ohrmarke15 || digits;
        }
        return (
            this.selectedTier?.ohrmarke_notnull ||
            this.selectedTier?.ohrmarke_kurz ||
            this.selectedTier?.ohrmarke15 ||
            digits
        );
    }

    private beginScanSession(rawDigits: string): string {
        this.eingabeCancel$.next();
        const digits = this.readDigitsFromInput(rawDigits);
        this.scanBusy = true;
        this.pendingScanDigits = digits;
        this.clearScanInputField();
        this.trailGuard.armTrailingIgnore();
        return digits;
    }

    private endScanSession(): void {
        this.eingabeCancel$.next();
        this.scanBusy = false;
        this.pendingScanDigits = '';
        this.selectedTier = undefined;
        this.clearScanInputField();
        this.trailGuard.armTrailingIgnore();
        this.focusInput();
    }

    private handleTierBereitsAufListe(scanDigits: string, tier?: GruppeTier): void {
        const scanOhrmarke = this.buildScanOhrmarkeAnzeige(scanDigits);
        if (tier) {
            this.selectedTier = tier;
        }
        this.pushScanErgebnis('warning', scanOhrmarke);
        this.messageService.hintMessage('Tier ist bereits in der Ausstallliste.');
        this.endScanSession();
    }

    private pushScanErgebnis(kind: 'ok' | 'warning' | 'error', _ohrmarke: string): void {
        if (kind === 'ok') {
            this.gruppenService.playScanSound(SoundType.OK);
            this.openCloseState = 'open';
            setTimeout(() => (this.openCloseState = 'closed'), 400);
            return;
        }
        if (kind === 'warning') {
            this.gruppenService.playScanSound(SoundType.WARNING);
            this.openCloseState = 'openWarning';
            setTimeout(() => (this.openCloseState = 'closedWarning'), 400);
            return;
        }
        this.gruppenService.playScanSound(SoundType.ERROR);
        this.openCloseState = 'openError';
        setTimeout(() => (this.openCloseState = 'closedError'), 400);
    }

    private checkOhrmarkenVorschau(): void {
        if (this.ohrmarken.length === 1) {
            this.selectedTier = this.ohrmarken[0];
            this.ohrmarke = this.selectedTier.ohrmarke_notnull || '';
            this.ohrmarkeGefunden = true;
            this.keinErgebnis = false;
            if (this.isTierAufAktuellerListe(this.selectedTier)) {
                this.checkBackgroundColor = 'red';
                this.checkColor = 'white';
                this.checkBorderColor = this.numbers.length > 4 ? 'red' : 'black';
                this.checkIcon = 'delete';
            } else {
                this.checkBackgroundColor = 'green';
                this.checkColor = 'white';
                this.checkBorderColor = this.numbers.length > 4 ? 'green' : 'black';
                this.checkIcon = 'check';
            }
        } else if (this.ohrmarken.length === 0) {
            this.selectedTier = undefined;
            this.ohrmarke = 'nicht vorhanden';
            this.ohrmarkeGefunden = false;
            this.keinErgebnis = this.numbers.length > 0;
            this.checkBackgroundColor = 'lightgray';
            this.checkColor = 'gray';
            this.checkBorderColor = this.numbers.length > 0 ? 'red' : 'black';
            this.checkIcon = '';
        } else if (this.ohrmarken.length > 1) {
            this.selectedTier = undefined;
            this.ohrmarke = 'nicht vorhanden';
            this.ohrmarkeGefunden = false;
            this.keinErgebnis = false;
            this.checkBackgroundColor = 'lightgray';
            this.checkColor = 'gray';
            this.checkBorderColor = 'black';
            this.checkIcon = '';
        }
        this.aktualisiereAnzeigeTiere();
    }

    private resetTrefferAnzeige(): void {
        this.ohrmarken = [];
        this.ohrmarkenAlle = [];
        this.ohrmarke = '';
        this.ohrmarkeGefunden = false;
        this.keinErgebnis = false;
        this.selectedTier = undefined;
        this.checkBackgroundColor = 'lightgray';
        this.checkColor = 'gray';
        this.checkBorderColor = 'black';
        this.checkIcon = '';
    }

    clearEingabe(): void {
        this.clearScanInputField();
        this.focusInput();
    }

    private clearScanInputField(): void {
        this.resetTrefferAnzeige();
        this.numbers = '';
        this.eingabeCtrl.setValue('', { emitEvent: false });
        this.calcFontSize();
        this.aktualisiereAnzeigeTiere();
    }

    private openWeightDialog(): void {
        const input = new EnterValueInput();
        input.title = 'Gewicht oder Abteil eingeben';
        input.text = 'Es kann optional ein Gewicht oder Abteil angegeben werden';
        input.okButton = 'Speichern';
        input.buttonColor = 'accent';
        input.addInputValue('Gewicht in kg (optional)', 'decimal');
        input.addInputValue('Abteil (optional)', 'text', this.abgangService.abteilAbgang);

        this.dialog
            .open(EnterValueDialogComponent, { data: input, position: { top: '10px' } })
            .afterClosed()
            .subscribe((response: EnterValueResponse) => {
                if (!response) {
                    this.endScanSession();
                    return;
                }
                if (response.result) {
                    this.abgangService.abteilAbgang = response.getValue(1);
                    this.tierZurListeHinzufuegen(response.getValue(0), response.getValue(1));
                } else {
                    this.tierZurListeHinzufuegen();
                }
            });
    }

    ausListeEntfernenTier(tier: GruppeTier): void {
        this.selectedTier = tier;
        this.ausListeEntfernen();
    }

    onScanTierCardReload(): void {
        this.ladeTierlisteTiere();
    }

    private ausListeEntfernen(): void {
        if (!this.selectedTier) {
            this.endScanSession();
            return;
        }
        const tierToRemove = this.resolveTierForListeEntfernung(this.selectedTier);
        const input = new EnterValueInput();
        input.title = 'Tier aus Liste entfernen?';
        input.text = 'Es kann optional ein Grund für das Entfernen eingetragen werden.';
        input.okButton = 'entfernen';
        input.buttonColor = 'accent';
        input.addInputValue('Grund (optional)', 'text');

        this.dialog
            .open(EnterValueDialogComponent, { data: input, position: { top: '10px' } })
            .afterClosed()
            .subscribe((res: EnterValueResponse) => {
                if (res?.result !== true) {
                    this.endScanSession();
                    return;
                }
                if (KieneUtils.isNNoU(res.getValue(0))) {
                    tierToRemove.grund_entfernung = res.getValue(0);
                }
                this.api.post(this.apiBaseUrl + 'gruppe_tier/delete_tierliste.php', tierToRemove, undefined).subscribe({
                    next: () => {
                        this.messageService.infoMessage('Das Tier wurde erfolgreich aus der Liste entfernt!');
                        const gtId = Number(tierToRemove.gt_id);
                        const removedTier = tierToRemove;
                        this.markTierInListenAnsicht(gtId, null);
                        this.adjustAlleTiereZurListeVerfuegbarCount(1);
                        this.removeTierFromAusstalllisteAnsicht(gtId);
                        const updatedListe = {
                            ...this.abgangsliste,
                            anzahl_tiere: Math.max(0, Number(this.abgangsliste?.anzahl_tiere ?? 1) - 1),
                        };
                        this.abgangsliste = updatedListe;
                        this.abgangService.setAbgangsliste(updatedListe, false, false, true, removedTier);
                        this.endScanSession();
                        this.cdr.markForCheck();
                    },
                    error: err => {
                        this.messageService.errorMessage('Fehler!', err);
                        this.endScanSession();
                    },
                });
            });
    }

    private shouldPreserveBrowseListOnAdd(): boolean {
        return this.tierListeModus === 'alle' || this.tierListeModus === 'teil';
    }

    /**
     * Browse rows may lack tierliste_id while still being on the Ausstallliste (right column).
     * delete_tierliste.php needs gt_id + tierliste_id like the scan-list row.
     */
    private resolveTierForListeEntfernung(tier: GruppeTier): GruppeTier {
        const gtId = Number(tier?.gt_id || 0);
        const listeId = Number(this.abgangsliste?.tierliste_id || 0);
        const fromAusstallliste = this.tierlisteTiere.find(t => Number(t?.gt_id) === gtId);
        if (fromAusstallliste) {
            return {
                ...fromAusstallliste,
                ...tier,
                gt_id: gtId,
                tierliste_id: Number(fromAusstallliste.tierliste_id || listeId) || listeId,
            };
        }
        if (listeId > 0) {
            return { ...tier, gt_id: gtId, tierliste_id: listeId };
        }
        return tier;
    }

    /** Keep left-column tierliste_id in sync with the loaded Ausstallliste (for row highlight). */
    private syncBrowseListTierlisteIdsFromTierlisteTiere(): void {
        const listeId = Number(this.abgangsliste?.tierliste_id || 0);
        if (listeId <= 0) {
            return;
        }
        for (const tier of this.tierlisteTiere) {
            const gtId = Number(tier?.gt_id || 0);
            if (gtId > 0) {
                this.markTierInListenAnsicht(gtId, listeId);
            }
        }
    }

    private clearBrowseListTierlisteIds(): void {
        const clearList = (list: GruppeTier[]) => {
            for (let i = 0; i < list.length; i++) {
                if (list[i]?.tierliste_id != null) {
                    list[i] = { ...list[i], tierliste_id: undefined };
                }
            }
        };
        clearList(this.gruppenTiere);
        clearList(this.ohrmarken);
        clearList(this.ohrmarkenAlle);
        if (this.anzeigeTiere !== this.gruppenTiere) {
            clearList(this.anzeigeTiere);
        }
    }

    private markTierInListenAnsicht(gtId: number, tierlisteId: number | null): void {
        const patchList = (list: GruppeTier[]) => {
            const idx = list.findIndex(t => Number(t?.gt_id) === gtId);
            if (idx < 0) {
                return;
            }
            list[idx] = {
                ...list[idx],
                tierliste_id: tierlisteId != null ? tierlisteId : undefined,
            };
        };
        patchList(this.gruppenTiere);
        patchList(this.ohrmarken);
        patchList(this.ohrmarkenAlle);
        if (this.anzeigeTiere !== this.gruppenTiere) {
            patchList(this.anzeigeTiere);
        }
    }

    /** Update the right-hand list without refetching (avoids scroll jump). */
    private prependTierZurAusstalllisteAnsicht(tier: GruppeTier, tierliste: Tierliste): void {
        const patched: GruppeTier = {
            ...tier,
            tierliste_id: tierliste.tierliste_id,
        };
        const gtId = Number(patched.gt_id || 0);
        const ohneDuplikat = this.tierlisteTiere.filter(t => Number(t.gt_id) !== gtId);
        this.tierlisteTiere = [patched, ...ohneDuplikat].slice(0, this.tierPageSize);
        if (this.abgangsliste) {
            this.abgangsliste = { ...this.abgangsliste, anzahl_tiere: tierliste.anzahl_tiere };
        }
        this.markTierInListenAnsicht(gtId, tierliste.tierliste_id);
    }

    private removeTierFromAusstalllisteAnsicht(gtId: number): void {
        this.tierlisteTiere = this.tierlisteTiere.filter(t => Number(t.gt_id) !== gtId);
    }

    private captureDialogScrollTop(): number {
        return this.getDialogScrollContainer()?.scrollTop ?? 0;
    }

    private restoreDialogScrollTop(scrollTop: number): void {
        const container = this.getDialogScrollContainer();
        if (!container) {
            return;
        }
        requestAnimationFrame(() => {
            container.scrollTop = scrollTop;
        });
    }

    private getDialogScrollContainer(): HTMLElement | null {
        return this.host.nativeElement.closest(
            '.mat-mdc-dialog-content, .mat-dialog-content',
        ) as HTMLElement | null;
    }

    private calcFontSize(): void {
        if (this.numbers.length > 12) {
            this.fontSize = '1.4em';
        } else if (this.numbers.length > 8) {
            this.fontSize = '2em';
        } else {
            this.fontSize = '2.5em';
        }
    }

    private focusInput(): void {
        setTimeout(() => {
            this.tierEingabeInput?.nativeElement?.focus({ preventScroll: true });
        }, 0);
    }

    private isKeyEventFromInput(event: KeyboardEvent): boolean {
        if (event.target instanceof Node) {
            const name = event.target.nodeName;
            return name === 'INPUT' || name === 'SELECT' || name === 'TEXTAREA';
        }
        return false;
    }

    getFirstTenDigits(ohrmarke: string): string {
        return ohrmarke.length > 5 ? ohrmarke.substring(0, ohrmarke.length - 5) : ohrmarke;
    }

    getLastFiveDigits(ohrmarke: string): string {
        return ohrmarke.length > 5 ? ohrmarke.substring(ohrmarke.length - 5) : '';
    }

    get canShowTierFilterButton(): boolean {
        return !!this.filterDialogGruppe?.gruppe_id;
    }

    get filterGruppeIdsForDialog(): number[] {
        return this.getEffectiveBrowseGruppeIds();
    }

    private createInitialFilterSettings(): TierFilterSettings {
        const tfs = new TierFilterSettings();
        tfs.showAbgangslisten = false;
        tfs.showAbteile = true;
        tfs.showEinstalldatum = true;
        tfs.showFilter = true;
        tfs.showGeschlechter = true;
        tfs.showRassen = true;
        tfs.showReklamationen = true;
        tfs.showViehhaendler = true;
        tfs.showBetriebskontakte = false;
        tfs.showHerkunftsbetriebe = false;
        return tfs;
    }

    private updateFilterDialogGruppe(): void {
        const gruppeId =
            Number(this.filterGruppeId || 0) ||
            Number(this.getEffectiveBrowseGruppeIds()[0] || 0);
        if (gruppeId <= 0) {
            this.filterDialogGruppe = null;
            return;
        }
        const gruppe = new Gruppe();
        gruppe.gruppe_id = gruppeId;
        this.filterDialogGruppe = gruppe;
    }

    abgangslisteLeeren(): void {
        if (!this.canAbgangslisteLeeren || !this.abgangsliste?.tierliste_id) {
            return;
        }

        const input = new ConfirmDialogInput();
        input.headline = 'Liste leeren?';
        input.text = 'Wollen Sie die aktuelle Ausstallliste wirklich leeren?';
        input.okButton = 'Leeren';
        input.buttonColor = 'warn';

        const dialogRef = this.dialog.open(ConfirmDialogComponent, { data: input, disableClose: true });
        dialogRef.afterClosed().subscribe({
            next: (result: ConfirmDialogResult) => {
                if (!result?.confirmed) {
                    return;
                }
                this.appService.setBlockOnLoading(true);
                this.api
                    .post(this.apiBaseUrl + 'gruppe_tier/delete_tierliste.php', this.abgangsliste, undefined)
                    .subscribe({
                        next: () => {
                            this.appService.setBlockOnLoading(false);
                            this.tierlisteTiere = [];
                            this.clearBrowseListTierlisteIds();
                            this.openCloseState = 'closed';
                            this.messageService.infoMessage('Die Ausstallliste wurde erfolgreich geleert!');
                            const clearedListe: Tierliste = { ...this.abgangsliste, anzahl_tiere: 0 };
                            this.abgangsliste = clearedListe;
                            this.abgangService.setAbgangsliste(clearedListe, true, true);
                            if (this.abgangsliste?.gruppe_id_abgang) {
                                this.abgangService.gruppeAktualisieren(this.abgangsliste.gruppe_id_abgang);
                            }
                            this.aktualisiereAnzeigeTiere();
                            this.cdr.markForCheck();
                        },
                        error: err => {
                            this.appService.setBlockOnLoading(false);
                            this.messageService.errorMessage('Fehler!', err);
                            this.cdr.markForCheck();
                        },
                    });
            },
        });
    }

    alleTiereZurAbgangslisteHinzufuegen(): void {
        if (this.isGlobalTierliste) {
            return;
        }
        const gruppeId = this.getGruppeIdForBulkAdd();
        const tierlisteId = Number(this.abgangsliste?.tierliste_id || 0);
        if (gruppeId <= 0 || tierlisteId <= 0 || this.alleTiereZurListeLaeuft || !this.canAlleTiereZurListeHinzufuegen) {
            return;
        }

        this.alleTiereZurListeLaeuft = true;
        const params = new HttpParams().set('gruppe_id', String(gruppeId)).set('lebendig_nicht_abgegangen', '1');
        this.api.getAll(this.apiBaseUrl + 'gruppe_tier/read_scan_kundenportal.php', params).subscribe({
            next: response => {
                const tiereNichtInListe = response.records || [];

                if (tiereNichtInListe.length === 0) {
                    this.alleTiereZurListeLaeuft = false;
                    this.messageService.infoMessage('Alle verfügbaren Tiere sind bereits in der Liste!');
                    return;
                }

                const obj = {
                    tierliste_id: tierlisteId,
                    gruppen_tiere: tiereNichtInListe,
                    gruppe_id: gruppeId,
                };
                this.api.post(this.apiBaseUrl + 'tierliste/abgang_kundenportal.php', obj, undefined).subscribe({
                    next: (tierlisteAntwort: Tierliste) => {
                        this.alleTiereZurListeLaeuft = false;
                        this.alleTiereZurListeVerfuegbarCount = 0;
                        this.messageService.infoMessage(
                            tiereNichtInListe.length + ' Tiere wurden zur aktuellen Ausstallliste hinzugefügt.',
                        );
                        this.abgangService.setAbgangsliste(tierlisteAntwort, true, true);
                        if (gruppeId > 0) {
                            this.abgangService.gruppeAktualisieren(gruppeId);
                        }
                        this.cdr.markForCheck();
                    },
                    error: err => {
                        this.alleTiereZurListeLaeuft = false;
                        this.messageService.errorMessage('Fehler beim Hinzufügen der Tiere!', err);
                        this.cdr.markForCheck();
                    },
                });
            },
            error: err => {
                this.alleTiereZurListeLaeuft = false;
                this.messageService.errorMessage('Fehler beim Laden der Tiere!', err);
                this.cdr.markForCheck();
            },
        });
    }
}
